import BotonManualHeader from "../../components/BotonManualHeader";
import React, { useState, useEffect } from "react";
import { Container, Card, Form, Button, Row, Col, Badge, Spinner, Alert } from "react-bootstrap";
import { FaUserMd, FaIdCard, FaUpload, FaCheckCircle, FaFileMedical, FaExclamationTriangle, FaCloudUploadAlt, FaSearch, FaTimes, FaFileAlt, FaTrash } from "react-icons/fa";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getFirestore, collection, setDoc, doc, getDoc, getDocs, serverTimestamp } from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import sendCustomEmail from "../../functions/sendCustomEmail";
import Swal from "sweetalert2";
import { logBusinessAction } from "../../services/AuditService";

const db = getFirestore(firebaseApp);

function EnviarExamenMedico({ usuario }) {
  const [dni, setDni] = useState("");
  const [tipoExamen, setTipoExamen] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [examenesMedicos, setExamenesMedicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [buscando, setBuscando] = useState(false);

  useEffect(() => {
    const cargarExamenes = async () => {
      try {
        setLoading(true);
        const rucEmpresa = usuario.ruc || "20538856674";
        const docsRef = collection(db, `onboarding_lista/${rucEmpresa}/documentos`);
        const snap = await getDocs(docsRef);
        const lista = [];
        snap.forEach(d => {
          const data = d.data();
          const titulo = (data.title || data.nombre || d.id || "").toLowerCase();
          const idStr = String(d.id);
          const esMedico = idStr === "9" || idStr === "32" ||
            titulo.includes("médico") || titulo.includes("médica") ||
            titulo.includes("salud") || titulo.includes("emo") ||
            titulo.includes("examen") || titulo.includes("clínico") ||
            titulo.includes("ocupacional");
          if (esMedico) {
            lista.push({
              id: d.id,
              name: data.title || data.nombre || data.tipo || d.id,
              type: data.type,
              description: data.description || "",
              obligatorio: data.obligatorio || false,
            });
          }
        });
        lista.sort((a, b) => (parseInt(a.id) || 0) - (parseInt(b.id) || 0));
        if (lista.length === 0) {
          setLoadError("No se encontraron exámenes médicos en la lista maestra");
        }
        setExamenesMedicos(lista);
      } catch (e) {
        setLoadError("Error al cargar exámenes médicos: " + e.message);
      } finally {
        setLoading(false);
      }
    };
    cargarExamenes();
  }, [usuario.ruc]);

  const buscarEmpleado = async () => {
    if (!dni.trim() || dni.length < 8) {
      setError("Ingrese un DNI válido");
      setUserData(null);
      return;
    }
    try {
      setBuscando(true);
      setError(null);
      const userRef = doc(db, "users", dni.trim());
      const snap = await getDoc(userRef);
      if (snap.exists()) {
        setUserData(snap.data());
        setError(null);
      } else {
        setError(`No se encontró empleado con DNI: ${dni}`);
        setUserData(null);
      }
    } catch (e) {
      setError("Error al buscar empleado: " + e.message);
      setUserData(null);
    } finally {
      setBuscando(false);
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      setError("Solo se permiten archivos PDF");
      setFile(null);
      return;
    }
    setFile(f);
    setError(null);
    setSuccess(false);
  };

  const removeFile = () => {
    setFile(null);
    document.getElementById("fileInput").value = "";
  };

  const handleUpload = async () => {
    if (!dni.trim()) { setError("Ingrese el DNI del empleado"); return; }
    if (!tipoExamen) { setError("Seleccione el tipo de examen"); return; }
    if (!file) { setError("Seleccione el archivo PDF del examen"); return; }
    if (!userData) await buscarEmpleado();

    setUploading(true);
    setError(null);
    setSuccess(false);
    setUploadProgress(0);

    try {
      const storage = getStorage(firebaseApp);
      const storagePath = `onboarding/${dni.trim()}/${tipoExamen}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, storagePath);

      const uploadResult = await uploadBytes(storageRef, file);
      setUploadProgress(50);
      const downloadURL = await getDownloadURL(uploadResult.ref);
      setUploadProgress(70);

      const examenDoc = examenesMedicos.find(e => e.id === tipoExamen);
      const nombreExamen = examenDoc?.name || "Examen Médico";
      const nombreEmpresa = usuario.empresaNombreComercial || usuario.empresa || "";

      const docRef = doc(db, "onboarding", dni.trim(), "documentos", tipoExamen);
      await setDoc(docRef, {
        id: tipoExamen,
        title: nombreExamen,
        nombre: nombreExamen,
        type: examenDoc?.type || "upload",
        link: downloadURL,
        storagePath,
        completed: true,
        activo: true,
        obligatorio: examenDoc?.obligatorio || false,
        fechaSubida: serverTimestamp(),
        subidoPor: usuario.dni || "medico",
        rolSubida: "medico_ocupacional",
      }, { merge: true });

      const rucEmpresa = usuario.ruc || "20538856674";
      const currentDate = new Date();

      const legajoCollectionRef = collection(db, "onboarding", dni.trim(), "legajo");
      // El ID se genera en el cliente: el documento y su campo `id` se
      // escriben en una sola operación (antes: addDoc + setDoc = 2 viajes).
      const legajoDocRef = doc(legajoCollectionRef);
      await setDoc(legajoDocRef, {
        id: legajoDocRef.id,
        activo: true,
        ano: currentDate.getFullYear(),
        contador: 0,
        dni: dni.trim(),
        fechaDoc: serverTimestamp(),
        fechaVista: "",
        link: downloadURL,
        mes: String(currentDate.getMonth() + 1).padStart(2, "0"),
        nombre: nombreExamen,
        ruc: rucEmpresa,
        tipo: nombreExamen,
        procesadoPor: usuario.email || usuario.nombre || "medico",
        documentId: tipoExamen,
        estado: "pendiente",
        storagePath: storagePath,
        storagePathError: false,
      });

      setUploadProgress(85);

      const emailDestino = userData?.email || usuario.email;
      const cc = userData?.recursosHumanos || null;
      const bcc = "miboleta@perubi.com";
      const asunto = `${nombreEmpresa} - Resultado de ${nombreExamen}`;
      const body = `Estimado(a) ${userData?.name || "colaborador"} ${userData?.apellidos || ""},\n\n` +
        `Su médico ocupacional ha registrado un nuevo resultado de examen: ${nombreExamen}.\n` +
        `Por favor ingrese al aplicativo Mi Boleta para revisarlo.\n\n` +
        `Saludos cordiales,\n${nombreEmpresa}`;

      await sendCustomEmail(emailDestino, cc, bcc, asunto, body, "mailTurbo");
      setUploadProgress(100);

      try {
        await logBusinessAction("ENVIO_EXAMEN_MEDICO", "GestionOnboarding", {
          cambiosRealizados: {
            envio: { anterior: null, nuevo: `${nombreExamen} para DNI ${dni}` }
          },
          tipoDocumento: nombreExamen,
        }, usuario);
      } catch (e) {
        console.warn("No se pudo registrar auditoría", e);
      }

      setSuccess(true);
      setFile(null);
      document.getElementById("fileInput").value = "";
      setTipoExamen("");

      Swal.fire({
        title: "¡Examen enviado!",
        text: `El examen ${nombreExamen} fue enviado exitosamente a ${userData?.name || dni}.`,
        icon: "success",
      });
    } catch (e) {
      setError(`Error al enviar: ${e.message}`);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const examenSeleccionado = examenesMedicos.find(e => e.id === tipoExamen);

  return (
    <div className="min-vh-100" style={{
      background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
      minHeight: "100vh", width: "100vw", maxWidth: "100%", padding: "20px 0",
    }}>
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        <Card className="shadow-lg border-0" style={{ borderRadius: "15px", overflow: "hidden", minHeight: "90vh" }}>
          <Card.Header className="text-white py-4" style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderBottom: "3px solid #f39c12",
          }}>
            <div className="d-flex align-items-center" style={{position:"relative"}}>
              <div className="text-center flex-grow-1">
                <h2 className="fw-bold mb-1">
                  <FaUserMd className="me-3" />
                  Enviar Examen Médico
                </h2>
                <p className="mb-0 opacity-75">
                  Envío de resultados de exámenes ocupacionales a colaboradores
                </p>
              </div>
              <BotonManualHeader usuario={usuario} />
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {uploading && (
              <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                style={{ zIndex: 1080, backgroundColor: "rgba(0, 0, 0, 0.8)", backdropFilter: "blur(5px)" }}>
                <Card className="shadow-lg border-0 text-center" style={{ borderRadius: "15px", minWidth: "300px", padding: "20px" }}>
                  <Card.Body>
                    <Spinner animation="border" variant="primary" style={{ width: "3rem", height: "3rem" }} />
                    <h5 className="mt-3 mb-3">Enviando examen médico...</h5>
                    <div className="progress mb-3" style={{ height: "12px", borderRadius: "10px" }}>
                      <div className="progress-bar progress-bar-striped progress-bar-animated" role="progressbar"
                        style={{ width: `${uploadProgress}%`, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}
                        aria-valuenow={uploadProgress} aria-valuemin="0" aria-valuemax="100">
                        {Math.round(uploadProgress)}%
                      </div>
                    </div>
                    <p className="text-muted mb-0">Procesando envío...</p>
                  </Card.Body>
                </Card>
              </div>
            )}

            {error && (
              <Alert variant="danger" className="border-0 shadow-sm mb-4" style={{ borderRadius: "10px" }}>
                <div className="d-flex align-items-center">
                  <FaExclamationTriangle className="me-3 fs-4" />
                  <div>
                    <h6 className="fw-bold mb-1">Error</h6>
                    <p className="mb-0">{error}</p>
                  </div>
                </div>
              </Alert>
            )}

            {success && (
              <Alert variant="success" className="border-0 shadow-sm mb-4" style={{ borderRadius: "10px" }}>
                <div className="d-flex align-items-center">
                  <FaCheckCircle className="me-3 fs-4" />
                  <div>
                    <h6 className="fw-bold mb-1">Examen enviado correctamente</h6>
                    <p className="mb-0">El empleado recibirá una notificación con el resultado.</p>
                  </div>
                </div>
              </Alert>
            )}

            {loadError && (
              <Alert variant="warning" className="border-0 shadow-sm mb-4" style={{ borderRadius: "10px" }}>
                <div className="d-flex align-items-center">
                  <FaExclamationTriangle className="me-3 fs-4" />
                  <div>
                    <h6 className="fw-bold mb-1">Exámenes no disponibles</h6>
                    <p className="mb-0">{loadError}</p>
                    <p className="mb-0 mt-2">
                      Configure exámenes médicos en la{" "}
                      <Button as="a" href="/ConfiguracionDocumentos" variant="link" className="p-0 fw-bold text-decoration-none">
                        Lista Maestra de Documentos
                      </Button>
                    </p>
                  </div>
                </div>
              </Alert>
            )}

            {!loading && !loadError && (
              <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                <Card.Header className="bg-light border-0 py-3">
                  <h5 className="mb-0 fw-semibold">
                    <FaIdCard className="me-2 text-primary" />
                    Empleado
                  </h5>
                </Card.Header>
                <Card.Body className="p-4">
                  <Row className="align-items-end">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold text-dark">DNI del Empleado</Form.Label>
                        <div className="d-flex gap-2">
                          <Form.Control
                            type="text" value={dni}
                            onChange={(e) => { setDni(e.target.value); setUserData(null); }}
                            placeholder="Ingrese DNI" maxLength={9}
                            disabled={uploading} className="form-control-lg" style={{ borderRadius: "10px" }}
                          />
                          <Button
                            variant="primary" onClick={buscarEmpleado}
                            disabled={uploading || buscando || !dni.trim()}
                            className="d-flex align-items-center px-3" style={{ borderRadius: "10px" }}>
                            {buscando ? <Spinner animation="border" size="sm" /> : <FaSearch />}
                            <span className="ms-2">Buscar</span>
                          </Button>
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      {userData ? (
                        <div className="text-success fw-semibold small">
                          <FaCheckCircle className="me-1" /> {userData.name} {userData.apellidos}
                        </div>
                      ) : dni && !error && (
                        <div className="text-muted small">Presione "Buscar" para ver datos del empleado</div>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}

            {!loading && !loadError && (
              <>
                <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                  <Card.Header className="bg-light border-0 py-3">
                    <h5 className="mb-0 fw-semibold">
                      <FaFileMedical className="me-2 text-primary" />
                      Tipo de Examen Médico
                    </h5>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <Form.Group>
                      <Form.Select
                        value={tipoExamen}
                        onChange={(e) => setTipoExamen(e.target.value)}
                        disabled={uploading}
                        className="form-control-lg" style={{ borderRadius: "10px" }}
                      >
                        <option value="">Seleccione un tipo de examen</option>
                        {examenesMedicos.map(e => (
                          <option key={e.id} value={e.id}>{e.id} - {e.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Card.Body>
                </Card>

                {examenSeleccionado && (
                  <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px" }}>
                    <Card.Body className="p-4">
                      <Row className="align-items-center">
                        <Col xs="auto">
                          <div style={{
                            width: "48px", height: "48px",
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            color: "white", borderRadius: "12px",
                            display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.25rem",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)"
                          }}>
                            <FaFileMedical />
                          </div>
                        </Col>
                        <Col>
                          <h4 className="mb-2 fw-bold">{examenSeleccionado.name}</h4>
                          <p className="text-muted mb-3">
                            {examenSeleccionado.description || "Documento médico para envío al colaborador"}
                          </p>
                          <div className="d-flex gap-2">
                            <Badge bg="primary" className="px-3 py-2" style={{ borderRadius: "8px" }}>
                              <FaFileAlt className="me-1" /> Examen Médico
                            </Badge>
                            {examenSeleccionado.obligatorio && (
                              <Badge bg="danger" className="px-3 py-2" style={{ borderRadius: "8px" }}>
                                Obligatorio
                              </Badge>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                )}

                {examenSeleccionado && (
                  <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                    <Card.Header className="bg-primary text-white py-3">
                      <div className="d-flex align-items-center">
                        <FaUpload className="me-2" />
                        <div>
                          <h5 className="mb-0 fw-semibold">Carga de resultado de examen</h5>
                          <small className="opacity-75">Seleccione el archivo PDF del examen médico</small>
                        </div>
                      </div>
                    </Card.Header>
                    <Card.Body className="p-4">
                      <Form.Group className="mb-4">
                        <Form.Label className="fw-semibold text-dark">
                          <FaCloudUploadAlt className="me-2" />
                          Archivo PDF
                        </Form.Label>
                        <Form.Control
                          id="fileInput" type="file" accept=".pdf"
                          onChange={handleFileChange}
                          disabled={uploading}
                          className="form-control-lg"
                          style={{ borderRadius: "10px", border: "2px dashed #dee2e6", padding: "20px" }}
                        />
                        <Form.Text className="text-muted">
                          <strong>Formato requerido:</strong> DNI-TITULO.pdf (ejemplo: 41607220-Examen-Medico.pdf)
                          <br />
                          Solo se permiten archivos PDF con el resultado del examen médico.
                        </Form.Text>
                      </Form.Group>

                      {file && (
                        <Card className="bg-light border-0 mb-4" style={{ borderRadius: "10px" }}>
                          <Card.Header className="bg-transparent border-0 py-3">
                            <h6 className="mb-0 fw-semibold">
                              <FaCheckCircle className="me-2 text-success" />
                              Archivo seleccionado
                            </h6>
                          </Card.Header>
                          <Card.Body className="p-3">
                            <div className="bg-white rounded-3 p-3">
                              <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center">
                                  <FaFileAlt className="text-primary me-3" size={16} />
                                  <div>
                                    <div className="fw-medium">{file.name}</div>
                                    <small className="text-muted">{(file.size / 1024).toFixed(1)} KB</small>
                                  </div>
                                </div>
                                <Button variant="outline-danger" size="sm" onClick={removeFile} style={{ borderRadius: "6px" }}>
                                  <FaTrash />
                                </Button>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      )}

                      <div className="d-flex justify-content-end pt-3 border-top">
                        <Button
                          variant="primary" size="lg"
                          onClick={handleUpload}
                          disabled={uploading || !dni || !tipoExamen || !file}
                          className="d-flex align-items-center px-4 py-3"
                          style={{
                            borderRadius: "10px",
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            border: "none",
                          }}
                        >
                          {uploading ? (
                            <><Spinner animation="border" size="sm" className="me-2" /> Enviando...</>
                          ) : (
                            <><FaUpload className="me-2" /> Enviar Examen Médico</>
                          )}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                )}

                {!examenSeleccionado && (
                  <Card className="shadow-sm border-0" style={{ borderRadius: "15px" }}>
                    <Card.Body className="py-5">
                      <div className="text-center">
                        <div className="bg-light rounded-circle mx-auto mb-3" style={{ width: "80px", height: "80px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <FaCloudUploadAlt size={32} className="text-muted" />
                        </div>
                        <h5 className="text-muted mb-2">Seleccione un tipo de examen para comenzar</h5>
                        <p className="text-muted">Elija el tipo de examen médico que desea enviar al colaborador</p>
                      </div>
                    </Card.Body>
                  </Card>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default EnviarExamenMedico;
